"use strict";
Object.defineProperty(exports, "__esModule", { value: true });
exports.ZoomRenderer = void 0;
const tslib_1 = require("tslib");
const core_api_1 = require("@ikegps/core-api");
const mobx_1 = require("mobx");
const vector_1 = require("../../../math/vector");
const model_1 = require("../../../model");
const unit_1 = require("../../../unit/unit");
const util_draw_svg_1 = require("../../drawable/util.draw.svg");
const mithril_1 = require("../../mithril");
const zoom_style_1 = require("./zoom.style");
class ZoomRenderer {
    constructor(ctx) {
        this.zoomLevel = 1;
        this.width = 0;
        this.height = 0;
        this.useCrosshair = true;
        this.useWireCalipers = true;
        this.crosshairCenterGap = 4;
        this.width = ctx.size.width || 200;
        this.height = ctx.size.height || 200;
        this.zoomLevel = ctx.zoomLevel || 1;
        this.useCrosshair = ctx.useCrosshair == null ? true : ctx.useCrosshair;
    }
    get isShown() {
        if (this.dc == null) {
            return false;
        }
        const current = this.dc.state.selectedId;
        if (current == null) {
            return false;
        }
        return true;
    }
    calculateCoordinate(offset, which) {
        const max = this.dc.capture.resolution[which];
        const min = 0;
        let calculatedOffset = Math.max(min, offset);
        calculatedOffset = Math.min(max, calculatedOffset);
        calculatedOffset = calculatedOffset * this.zoomLevel - this[which] / 2;
        return -calculatedOffset;
    }
    getZoomDrawable(dc) {
        const selected = dc.selected;
        if (selected == null) {
            return null;
        }
        if (model_1.CollectionModelDrawableLine.isLine(selected)) {
            if (dc.state.selectedRegion === util_draw_svg_1.IkeDrawLabel.LABEL) {
                return selected.start;
            }
        }
        if (selected instanceof model_1.CollectionModelDrawableLabel &&
            selected.parent instanceof model_1.CollectionModelDrawableVerticalSeparation) {
            return null;
        }
        return selected;
    }
    getPixelOffset(wireSizeInches) {
        const wireSize = unit_1.IkeUnit.convert(wireSizeInches, unit_1.IkeUnit.METER, unit_1.IkeUnit.INCH, core_api_1.IkeUnitType.DISTANCE); //inches to meters
        const moveUpXY = new vector_1.Vector(0.0, -wireSize / 2.0); // in meters :zero in width, up 1/2 selected wire;
        const moveDownXY = new vector_1.Vector(0.0, wireSize / 2.0); // in meters :zero in width, down 1/2 selected wire;
        return { moveUpXY, moveDownXY };
    }
    getWirePx(wireSizeInches, drawMarkerX, drawMarkerY, geoDraw) {
        //the full size picture coordinates of the center of the zoom window
        const drawXY = new vector_1.Vector(drawMarkerX, drawMarkerY);
        const halfHeight = this.height / 2.0;
        const halfWidth = this.width / 2.0;
        //gets the moved pixel value of the full picture
        const geoMoveUp = geoDraw.movePoint(drawXY, this.getPixelOffset(wireSizeInches).moveUpXY);
        const geoMoveDown = geoDraw.movePoint(drawXY, this.getPixelOffset(wireSizeInches).moveDownXY);
        //calculates the equivalency in the zoom window
        const MoveUp = new vector_1.Vector(halfWidth, halfHeight - this.zoomLevel * Math.abs(drawMarkerY - geoMoveUp.y));
        const MoveDown = new vector_1.Vector(halfWidth, halfHeight + this.zoomLevel * Math.abs(drawMarkerY - geoMoveDown.y));
        return { MoveUp, MoveDown };
    }
    view(dc) {
        this.dc = dc;
        if (this.isShown === false) {
            return;
        }
        const drawable = this.getZoomDrawable(dc);
        if (drawable == null) {
            return;
        }
        this.useWireCalipers = this.dc.state.showCablemeasure;
        const offsetX = drawable.x;
        const offsetY = drawable.y;
        const capture = this.dc.capture;
        const src = capture.file.url;
        const resolution = capture.resolution;
        const theImage = (0, mithril_1.m)('img', {
            src,
            className: zoom_style_1.CSS.image,
            style: `transform: scale(${this.zoomLevel}); ` +
                `filter: url(#${dc.id}:brightness); ` +
                `left: ${this.calculateCoordinate(offsetX, 'width')}px; ` +
                `top: ${this.calculateCoordinate(offsetY, 'height')}px;`,
            width: resolution.width,
            height: resolution.height,
        });
        const containerChildren = [theImage];
        if (this.useCrosshair) {
            const halfHeight = this.height / 2;
            const halfWidth = this.width / 2;
            const crosshairElements = [];
            if (this.useWireCalipers) {
                // Left to center
                crosshairElements.push((0, mithril_1.m)('line', {
                    className: zoom_style_1.CSS.crosshairOuterline,
                    stroke: zoom_style_1.OUTERLINE_STROKE,
                    'stroke-width': zoom_style_1.OUTERLINE_STROKE_WIDTH,
                    x1: 0,
                    y1: halfHeight,
                    x2: halfWidth / 16, //halfWidth - this.crosshairCenterGap,
                    y2: halfHeight,
                }));
                crosshairElements.push((0, mithril_1.m)('line', {
                    className: zoom_style_1.CSS.crosshairInnerline,
                    stroke: zoom_style_1.INNERLINE_STROKE,
                    'stroke-width': zoom_style_1.INNERLINE_STROKE_WIDTH,
                    x1: 0,
                    y1: halfHeight,
                    x2: halfWidth / 16, //halfWidth - this.crosshairCenterGap,
                    y2: halfHeight,
                }));
                crosshairElements.push((0, mithril_1.m)('line', {
                    className: zoom_style_1.CSS.crosshairOuterline,
                    stroke: zoom_style_1.OUTERLINE_STROKE,
                    'stroke-width': zoom_style_1.OUTERLINE_STROKE_WIDTH,
                    x1: halfWidth - halfWidth / 16,
                    y1: halfHeight,
                    x2: halfWidth - this.crosshairCenterGap,
                    y2: halfHeight,
                }));
                crosshairElements.push((0, mithril_1.m)('line', {
                    className: zoom_style_1.CSS.crosshairInnerline,
                    stroke: zoom_style_1.INNERLINE_STROKE,
                    'stroke-width': zoom_style_1.INNERLINE_STROKE_WIDTH,
                    x1: halfWidth - halfWidth / 16,
                    y1: halfHeight,
                    x2: halfWidth - this.crosshairCenterGap,
                    y2: halfHeight,
                }));
                // Right to center
                crosshairElements.push((0, mithril_1.m)('line', {
                    className: zoom_style_1.CSS.crosshairOuterline,
                    stroke: zoom_style_1.OUTERLINE_STROKE,
                    'stroke-width': zoom_style_1.OUTERLINE_STROKE_WIDTH,
                    x1: this.width - halfWidth / 16, //halfWidth + this.crosshairCenterGap,
                    y1: halfHeight,
                    x2: this.width,
                    y2: halfHeight,
                }));
                crosshairElements.push((0, mithril_1.m)('line', {
                    className: zoom_style_1.CSS.crosshairInnerline,
                    stroke: zoom_style_1.INNERLINE_STROKE,
                    'stroke-width': zoom_style_1.INNERLINE_STROKE_WIDTH,
                    x1: this.width - halfWidth / 16, //halfWidth + this.crosshairCenterGap,
                    y1: halfHeight,
                    x2: this.width,
                    y2: halfHeight,
                }));
                crosshairElements.push((0, mithril_1.m)('line', {
                    className: zoom_style_1.CSS.crosshairOuterline,
                    stroke: zoom_style_1.OUTERLINE_STROKE,
                    'stroke-width': zoom_style_1.OUTERLINE_STROKE_WIDTH,
                    x1: halfWidth + this.crosshairCenterGap,
                    y1: halfHeight,
                    x2: halfWidth + halfWidth / 16,
                    y2: halfHeight,
                }));
                crosshairElements.push((0, mithril_1.m)('line', {
                    className: zoom_style_1.CSS.crosshairInnerline,
                    stroke: zoom_style_1.INNERLINE_STROKE,
                    'stroke-width': zoom_style_1.INNERLINE_STROKE_WIDTH,
                    x1: halfWidth + this.crosshairCenterGap,
                    y1: halfHeight,
                    x2: halfWidth + halfWidth / 16,
                    y2: halfHeight,
                }));
            }
            else {
                // Left to right
                crosshairElements.push((0, mithril_1.m)('line', {
                    className: zoom_style_1.CSS.crosshairOuterline,
                    stroke: zoom_style_1.OUTERLINE_STROKE,
                    'stroke-width': zoom_style_1.OUTERLINE_STROKE_WIDTH,
                    x1: 0,
                    y1: halfHeight,
                    x2: halfWidth - this.crosshairCenterGap,
                    y2: halfHeight,
                }));
                crosshairElements.push((0, mithril_1.m)('line', {
                    className: zoom_style_1.CSS.crosshairInnerline,
                    stroke: zoom_style_1.INNERLINE_STROKE,
                    'stroke-width': zoom_style_1.INNERLINE_STROKE_WIDTH,
                    x1: 0,
                    y1: halfHeight,
                    x2: halfWidth - this.crosshairCenterGap,
                    y2: halfHeight,
                }));
                // Right to left
                crosshairElements.push((0, mithril_1.m)('line', {
                    className: zoom_style_1.CSS.crosshairOuterline,
                    stroke: zoom_style_1.OUTERLINE_STROKE,
                    'stroke-width': zoom_style_1.OUTERLINE_STROKE_WIDTH,
                    x1: halfWidth + this.crosshairCenterGap,
                    y1: halfHeight,
                    x2: this.width,
                    y2: halfHeight,
                }));
                crosshairElements.push((0, mithril_1.m)('line', {
                    className: zoom_style_1.CSS.crosshairInnerline,
                    stroke: zoom_style_1.INNERLINE_STROKE,
                    'stroke-width': zoom_style_1.INNERLINE_STROKE_WIDTH,
                    x1: halfWidth + this.crosshairCenterGap,
                    y1: halfHeight,
                    x2: this.width,
                    y2: halfHeight,
                }));
            }
            // Top to bottom
            crosshairElements.push((0, mithril_1.m)('line', {
                className: zoom_style_1.CSS.crosshairOuterline,
                stroke: zoom_style_1.OUTERLINE_STROKE,
                'stroke-width': zoom_style_1.OUTERLINE_STROKE_WIDTH,
                x1: halfWidth,
                y1: 0,
                x2: halfWidth,
                y2: halfHeight - this.crosshairCenterGap,
            }));
            crosshairElements.push((0, mithril_1.m)('line', {
                className: zoom_style_1.CSS.crosshairInnerline,
                stroke: zoom_style_1.INNERLINE_STROKE,
                'stroke-width': zoom_style_1.INNERLINE_STROKE_WIDTH,
                x1: halfWidth,
                y1: 0,
                x2: halfWidth,
                y2: halfHeight - this.crosshairCenterGap,
            }));
            // Bottom to top
            crosshairElements.push((0, mithril_1.m)('line', {
                className: zoom_style_1.CSS.crosshairOuterline,
                stroke: zoom_style_1.OUTERLINE_STROKE,
                'stroke-width': zoom_style_1.OUTERLINE_STROKE_WIDTH,
                x1: halfWidth,
                y1: halfHeight + this.crosshairCenterGap,
                x2: halfWidth,
                y2: this.height,
            }));
            crosshairElements.push((0, mithril_1.m)('line', {
                className: zoom_style_1.CSS.crosshairInnerline,
                stroke: zoom_style_1.INNERLINE_STROKE,
                'stroke-width': zoom_style_1.INNERLINE_STROKE_WIDTH,
                x1: halfWidth,
                y1: halfHeight + this.crosshairCenterGap,
                x2: halfWidth,
                y2: this.height,
            }));
            const crosshairSVG = (0, mithril_1.m)('svg', {
                className: zoom_style_1.CSS.crosshair,
                width: '100%',
                height: '100%',
                viewBox: '0 0 ' + this.width + ' ' + this.height,
            }, crosshairElements);
            containerChildren.push(crosshairSVG);
        }
        //VERSION 3
        if (this.useWireCalipers) {
            const center_x = this.width / 2;
            const caliperElements = [];
            const caliperLoc = this.getWirePx(dc.state.cablemeasure, drawable.x, drawable.y, dc.drawState.collection.state.getGeometry(drawable.captureId));
            const moveUp = caliperLoc.MoveUp;
            const moveDown = caliperLoc.MoveDown;
            //upper caliper
            caliperElements.push((0, mithril_1.m)('line', {
                className: zoom_style_1.CSS.crosshairOuterline,
                stroke: zoom_style_1.CALIPER_OUTERLINE_STROKE,
                'stroke-width': zoom_style_1.CALIPER_OUTERLINE_STROKE_WIDTH,
                x1: center_x / 16,
                y1: moveUp.y,
                x2: center_x - center_x / 16,
                y2: moveUp.y,
            }));
            caliperElements.push((0, mithril_1.m)('line', {
                className: zoom_style_1.CSS.crosshairInnerline,
                stroke: zoom_style_1.CALIPER_INNERLINE_STROKE,
                'stroke-width': zoom_style_1.CALIPER_INNERLINE_STROKE_WIDTH,
                x1: center_x / 16,
                y1: moveUp.y,
                x2: center_x - center_x / 16,
                y2: moveUp.y,
            }));
            caliperElements.push((0, mithril_1.m)('line', {
                className: zoom_style_1.CSS.crosshairOuterline,
                stroke: zoom_style_1.CALIPER_OUTERLINE_STROKE,
                'stroke-width': zoom_style_1.CALIPER_OUTERLINE_STROKE_WIDTH,
                x1: center_x + center_x / 16,
                y1: moveUp.y,
                x2: this.width - center_x / 16,
                y2: moveUp.y,
            }));
            caliperElements.push((0, mithril_1.m)('line', {
                className: zoom_style_1.CSS.crosshairInnerline,
                stroke: zoom_style_1.CALIPER_INNERLINE_STROKE,
                'stroke-width': zoom_style_1.CALIPER_INNERLINE_STROKE_WIDTH,
                x1: center_x + center_x / 16,
                y1: moveUp.y,
                x2: this.width - center_x / 16,
                y2: moveUp.y,
            }));
            //lower caliper
            caliperElements.push((0, mithril_1.m)('line', {
                className: zoom_style_1.CSS.crosshairOuterline,
                stroke: zoom_style_1.CALIPER_OUTERLINE_STROKE,
                'stroke-width': zoom_style_1.CALIPER_OUTERLINE_STROKE_WIDTH,
                x1: center_x / 16,
                y1: moveDown.y,
                x2: center_x - center_x / 16,
                y2: moveDown.y,
            }));
            caliperElements.push((0, mithril_1.m)('line', {
                className: zoom_style_1.CSS.crosshairInnerline,
                stroke: zoom_style_1.CALIPER_INNERLINE_STROKE,
                'stroke-width': zoom_style_1.CALIPER_INNERLINE_STROKE_WIDTH,
                x1: center_x / 16,
                y1: moveDown.y,
                x2: center_x - center_x / 16,
                y2: moveDown.y,
            }));
            caliperElements.push((0, mithril_1.m)('line', {
                className: zoom_style_1.CSS.crosshairOuterline,
                stroke: zoom_style_1.CALIPER_OUTERLINE_STROKE,
                'stroke-width': zoom_style_1.CALIPER_OUTERLINE_STROKE_WIDTH,
                x1: center_x + center_x / 16,
                y1: moveDown.y,
                x2: this.width - center_x / 16,
                y2: moveDown.y,
            }));
            caliperElements.push((0, mithril_1.m)('line', {
                className: zoom_style_1.CSS.crosshairInnerline,
                stroke: zoom_style_1.CALIPER_INNERLINE_STROKE,
                'stroke-width': zoom_style_1.CALIPER_INNERLINE_STROKE_WIDTH,
                x1: center_x + center_x / 16,
                y1: moveDown.y,
                x2: this.width - center_x / 16,
                y2: moveDown.y,
            }));
            caliperElements.push((0, mithril_1.m)('text', {
                className: zoom_style_1.CSS.crosshairCaliper,
                fill: 'white',
                stroke: 'black',
                x: center_x / 2,
                y: moveUp.y - 15,
                'text-anchor': 'middle',
                'font-size': 20,
                'alignment-baseline': 'middle',
                style: {
                    'user-select': 'none',
                    'pointer-events': 'none',
                    'font-weight': 'bold',
                },
            }, this.dc.state.cablemeasure + " '' "));
            const crosshairSVG = (0, mithril_1.m)('svg', {
                className: zoom_style_1.CSS.crosshair,
                width: '100%',
                height: '100%',
                viewBox: '0 0 ' + this.width + ' ' + this.height,
            }, caliperElements);
            containerChildren.push(crosshairSVG);
        }
        const theContainer = (0, mithril_1.m)('div', {
            className: zoom_style_1.CSS.container,
            style: 'width: ' + this.width + 'px; height: ' + this.height + 'px; overflow: hidden;',
        }, containerChildren);
        return theContainer;
    }
}
exports.ZoomRenderer = ZoomRenderer;
ZoomRenderer.CSS = zoom_style_1.CSS;
ZoomRenderer.Style = zoom_style_1.Style;
tslib_1.__decorate([
    mobx_1.observable
], ZoomRenderer.prototype, "zoomLevel", void 0);
tslib_1.__decorate([
    mobx_1.observable
], ZoomRenderer.prototype, "width", void 0);
tslib_1.__decorate([
    mobx_1.observable
], ZoomRenderer.prototype, "height", void 0);
tslib_1.__decorate([
    mobx_1.observable
], ZoomRenderer.prototype, "useCrosshair", void 0);
tslib_1.__decorate([
    mobx_1.observable
], ZoomRenderer.prototype, "useWireCalipers", void 0);
tslib_1.__decorate([
    mobx_1.observable
], ZoomRenderer.prototype, "crosshairCenterGap", void 0);
//# sourceMappingURL=zoom.render.js.map